import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "./MainLayout";
import {
  ChevronLeft, ChevronRight, Download, Printer,
  FileDown, X, Calendar, Menu, Filter, Search, Loader2, Home, CalendarDays
} from "lucide-react";
import "../styles/attendancereport.css";
import CalendarPopup from "./CalendarPopup";
import { useAuth } from "../context/AuthContext";
import { Tooltip } from "antd";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const API_BASE = process.env.REACT_APP_API_BASE_URL;
const PRESENT_HOURS_THRESHOLD = 8;

export default function AttendanceReportPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("Day");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState("CSV");
  const [showMenu, setShowMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calendarDays, setCalendarDays] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const { user, token, logout } = useAuth();

  const [leaveTypes, setLeaveTypes] = useState([]);

  const navigate = useNavigate();
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  const monthName = MONTHS[currentDate.getMonth()];
  const daysInMonth = new Date(year, month, 0).getDate();

  // ─── FORMAT: seconds → "HH:MM" (Zoho style) ──────────────────────
  const formatSecondsToHHMM = (totalSeconds) => {
    if (!totalSeconds || totalSeconds <= 0) return null;
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  // ─── FETCH FROM BACKEND ───────────────────────────────────────────
  const fetchAttendanceReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/attendance-report/report?month=${month}&year=${year}`
      );
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "API error");

      const apiData = json.data;
      setCalendarDays(apiData.calendar || []);

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      const mapped = apiData.employees.map((emp) => {
        const attendance = {};
        const days = new Date(year, month, 0).getDate();

        for (let day = 1; day <= days; day++) {
          const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isFutureDate = dateStr > todayStr;
          const dayData = emp.attendance[dateStr];

          if (dayData) {
            const totalSec = dayData.totalSeconds || dayData.total_seconds || 0;
            const hoursWorked = totalSec / 3600;
            let status = dayData.status;

            const calDay = apiData.calendar?.find((c) => c.day === day);
            const isWeekend = calDay ? calDay.isWeekend : (new Date(year, month - 1, day).getDay() === 0 || new Date(year, month - 1, day).getDay() === 6);
            const isHoliday = calDay ? calDay.isHoliday : false;

            if (!status || status === "-") {
              if (isFutureDate) {
                status = "-";
              } else {
                const totalSecVal = parseInt(dayData.totalSeconds) || 0;
                const hoursWorkedVal = totalSecVal / 3600;
                const hasCheckedIn = dayData.checkIn || dayData.check_in;

                if (totalSecVal > 0 || hasCheckedIn) {
                  if (totalSecVal <= 0 && hasCheckedIn) {
                    status = "PC";
                  } else if (hoursWorkedVal >= PRESENT_HOURS_THRESHOLD) {
                    status = "P";
                  } else if (hoursWorkedVal >= 4 && hoursWorkedVal < PRESENT_HOURS_THRESHOLD) {
                    if (dayData.leaveCode === "PERMISSION") {
                      status = "P";
                    } else {
                      status = (isWeekend || isHoliday) ? "P" : "A";
                    }
                  } else if (hoursWorkedVal > 0 && hoursWorkedVal < 4) {
                    status = (isWeekend || isHoliday) ? "P" : "A";
                  } else {
                    status = "P";
                  }
                } else if (isHoliday) {
                  status = "H";
                } else if (isWeekend) {
                  status = "W";
                } else {
                  status = "A";
                }
              }
            } else {
              const totalSecVal = parseInt(dayData.totalSeconds) || 0;
              const hasCheckedIn = dayData.checkIn || dayData.check_in;

              if ((status === "W" || status === "WO" || status === "H" || status === "Weekend" || status === "Holiday") && (totalSecVal > 0 || hasCheckedIn)) {
                if (totalSecVal <= 0 && hasCheckedIn) {
                  status = "PC";
                } else {
                  status = "P";
                }
              } else if (status === "WFH" || status === "P/WFH") {
                status = "P";
              } else if (status === "W") {
                status = "W";
              } else if (status === "H") {
                status = "H";
              } else if (status === "A" && isFutureDate) {
                status = "-";
              } else if (status === "L" || status === "HL") {
                const code = dayData.leaveCode || "L";
                const checkInTimeStr = dayData.checkIn || dayData.check_in || "";

                if (status === "HL" && checkInTimeStr) {
                  const hour = parseInt(checkInTimeStr.split(":")[0]) || 0;
                  if (hour >= 12) {
                    status = `H-${code}`; // For simplicity we use H-CODE here
                  } else {
                    status = `H-${code}`;
                  }
                } else {
                  status = code;
                }
              } else if (status === "PC") {
                if (totalSecVal / 3600 >= PRESENT_HOURS_THRESHOLD) {
                  status = "P";
                }
              }
            }

            attendance[day] = {
              status,
              totalSeconds: totalSec,
              hoursDisplay: formatSecondsToHHMM(totalSec),
              leaveCode: dayData.leaveCode || null,
              leaveName: dayData.leaveName || null,
            };
          } else {
            const calDay = apiData.calendar?.find((c) => c.day === day);
            const isWeekend = calDay ? calDay.isWeekend : (new Date(year, month - 1, day).getDay() === 0 || new Date(year, month - 1, day).getDay() === 6);
            const isHoliday = calDay ? calDay.isHoliday : false;
            let status = "-";
            if (!isFutureDate) {
              if (isHoliday) status = "H";
              else if (isWeekend) status = "W";
              else status = "A";
            }
            attendance[day] = { status: status, totalSeconds: 0, hoursDisplay: null };
          }
        }

        return {
          id: emp.empId,
          name: emp.fullName,
          avatar: emp.fullName.charAt(0).toUpperCase(),
          color: "#f97316",
          attendance,
          summary: emp.summary,
        };
      });

      const leaveTypeMap = {};
      mapped.forEach((emp) => {
        Object.values(emp.attendance).forEach((day) => {
          if ((day.status === "L" || day.status === "HL") && day.leaveCode) {
            leaveTypeMap[day.leaveCode] = day.leaveName || day.leaveCode;
          }
        });
      });
      setLeaveTypes(Object.entries(leaveTypeMap));

      setRawData(mapped);
      setFilteredData(mapped);
    } catch (err) {
      console.error("Attendance fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchAttendanceReport();
    setSearchQuery("");
    setFromDate("");
    setToDate("");

    const fetchHolidays = async () => {
      try {
        const holRes = await fetch(`${API_BASE || ''}/api/holidays`);
        if (holRes.ok) {
          const holData = await holRes.json();
          setHolidays(holData);
        }
      } catch (e) {
        console.error("Failed to fetch holidays", e);
      }
    };
    fetchHolidays();
  }, [fetchAttendanceReport]);

  // ─── SEARCH FILTER ────────────────────────────────────────────────
  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) { setFilteredData(rawData); return; }
    setFilteredData(
      rawData.filter(
        (emp) =>
          emp.name.toLowerCase().includes(query) ||
          String(emp.id).toLowerCase().includes(query)
      )
    );
  }, [searchQuery, rawData]);

  // ─── DATE RANGE FILTER ────────────────────────────────────────────
  const applyDateFilter = useCallback(() => {
    if (!fromDate || !toDate) { setFilteredData(rawData); return; }
    const from = new Date(fromDate);
    const to = new Date(toDate);

    const filtered = rawData
      .map((emp) => {
        const filteredAttendance = {};
        for (let day = 1; day <= daysInMonth; day++) {
          const date = new Date(year, month - 1, day);
          if (date >= from && date <= to) {
            filteredAttendance[day] = emp.attendance[day] || {
              status: "-", totalSeconds: 0, hoursDisplay: null,
            };
          }
        }
        return { ...emp, attendance: filteredAttendance };
      })
      .filter((emp) => Object.keys(emp.attendance).length > 0);

    setFilteredData(filtered);
  }, [rawData, fromDate, toDate, year, month, daysInMonth]);

  useEffect(() => { applyDateFilter(); }, [applyDateFilter]);

  // ─── MONTH NAV ────────────────────────────────────────────────────
  const prevMonth = () => {
    const d = new Date(currentDate); d.setMonth(d.getMonth() - 1); setCurrentDate(d);
  };
  const nextMonth = () => {
    const d = new Date(currentDate); d.setMonth(d.getMonth() + 1); setCurrentDate(d);
  };

  // ─── DAY INFO ─────────────────────────────────────────────────────
  const getDayInfo = (day) => {
    const calDay = calendarDays.find((c) => c.day === day);
    if (calDay) {
      return {
        date: `${monthName} ${String(day).padStart(2, "0")}`,
        day: calDay.dayName,
        isWeekend: calDay.isWeekend,
        isHoliday: calDay.isHoliday,
        holidayName: calDay.holidayName,
      };
    }
    const date = new Date(year, month - 1, day);
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return {
      date: `${monthName} ${String(day).padStart(2, "0")}`,
      day: dayNames[date.getDay()],
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      isHoliday: false,
      holidayName: null,
    };
  };

  // ─── STATUS STYLE ─────────────────────────────────────────────────
  const getStatusStyle = (status) => {
    switch (status) {
      case "P": return "status-present";
      case "PR": return "status-partial";
      case "A": return "status-absent";
      case "PC": return "status-pending-checkout";
      case "W": return "status-weekend";
      case "H": return "status-holiday";
      case "L": return "status-leave";
      case "HL": return "status-half-leave";
      default: return "status-default";
    }
  };

  // ─── RESET ────────────────────────────────────────────────────────
  const handleReset = () => {
    setFromDate(""); setToDate(""); setSearchQuery(""); setCurrentDate(new Date());
  };

  // ─── PDF EXPORT ───────────────────────────────────────────────────
  const downloadPDF = () => {
    const tableHTML = `
      <html><head><title>Attendance Report</title>
      <style>
        body{font-family:Arial,sans-serif;margin:20px;}
        h1{text-align:center;color:#333;}
        table{width:100%;border-collapse:collapse;margin-top:20px;font-size:11px;}
        th,td{border:1px solid #ddd;padding:5px;text-align:center;}
        th{background-color:#2980b9;color:white;font-weight:bold;}
        tr:nth-child(even){background-color:#f9f9f9;}
        .employee-name{text-align:left;font-weight:500;}
        .status-present{background-color:#d4edda;color:#155724;font-weight:bold;}
        .status-permission{background-color:#fff3cd;color:#856404;font-weight:bold;}
        .status-absent{background-color:#f8d7da;color:#721c24;font-weight:bold;}
        .status-pending-checkout{background-color:#fff3cd;color:#d39e00;font-weight:bold;}
        .status-weekend{background-color:#e2e3e5;color:#383d41;}
        .status-holiday{background-color:#cce5ff;color:#004085;font-weight:bold;}
        .info{font-size:12px;color:#666;margin-top:20px;}
      </style></head><body>
      <h1>Attendance Report - ${monthName} ${year}</h1>
      <table>
        <thead><tr>
          <th>Employee</th>
          ${Array.from({ length: daysInMonth }, (_, i) => {
      const info = getDayInfo(i + 1);
      const bg = info.isWeekend ? 'style="background-color:#95a5a6;"'
        : info.isHoliday ? 'style="background-color:#6baed6;"' : "";
      return `<th ${bg}>${i + 1}<br/>${info.day}</th>`;
    }).join("")}
        </tr></thead>
        <tbody>
          ${filteredData.map((emp) => `
            <tr>
              <td class="employee-name">${emp.name}</td>
              ${Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const d = emp.attendance[day];
      const status = d?.status || "-";
      const info = getDayInfo(day);
      const cls =
        status === "P" ? "status-present" :
          status === "PR" ? "status-permission" :
            status === "A" ? "status-absent" :
              status === "PC" ? "status-pending-checkout" :
                status === "W" ? "status-weekend" :
                  status === "H" ? "status-holiday" : "";
      const bgStyle = info.isWeekend ? "background-color:#e2e3e5;"
        : info.isHoliday ? "background-color:#cce5ff;" : "";
      return `<td class="${cls}" style="${bgStyle}">${status}</td>`;
    }).join("")}
            </tr>
          `).join("")}
        </tbody>
      </table>
      <div class="info">
        <p>Generated: ${new Date().toLocaleString()} | Total: ${filteredData.length} employees</p>
        ${fromDate && toDate ? `<p>Period: ${fromDate} to ${toDate}</p>` : ""}
      </div>
      </body></html>
    `;
    const pw = window.open("", "", "height=600,width=1000");
    pw.document.write(tableHTML);
    pw.document.close();
    setTimeout(() => { pw.print(); setShowMenu(false); }, 250);
  };

  // ─── CSV EXPORT ───────────────────────────────────────────────────
  const executeExport = () => {
    const headers = [
      "Emp ID", "Name",
      ...Array.from({ length: daysInMonth }, (_, i) => `Day ${i + 1}`),
    ];
    const rows = filteredData.map((emp) => {
      const dayCells = Array.from(
        { length: daysInMonth },
        (_, i) => emp.attendance[i + 1]?.status || "-"
      );
      return [emp.id, emp.name, ...dayCells];
    });
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `attendance_${monthName}_${year}.csv`; a.click();
    window.URL.revokeObjectURL(url);
    setShowExportModal(false); setShowMenu(false);
  };

  return (
    <>
      <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} title="Attendance Report" subtitle="View monthly attendance report">

        <div className="attendance-report-page">

          {/* ── Consolidated Filter Row ── */}
          <div className="ar-filter-row" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: '4px' }}>

            <div className="ar-filter-group ar-search-group" style={{ flex: '0 0 220px' }}>
              <Search size={14} />
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="ar-search-input"
              />
              {searchQuery && (
                <button className="ar-clear-search" onClick={() => setSearchQuery("")}>
                  <X size={12} />
                </button>
              )}
            </div>

            <div className="ar-filter-group" style={{ height: '36px', flexShrink: 0 }}>
              <label className="ar-filter-label" style={{ display: 'inline-block', marginRight: '4px' }}>From:</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="ar-date-input" title="From Date" />
            </div>

            <div className="ar-filter-group" style={{ height: '36px' }}>
              <label className="ar-filter-label" style={{ display: 'inline-block', marginRight: '4px' }}>To:</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="ar-date-input" title="To Date" />
            </div>

            <button className="ar-reset-btn" style={{ height: '36px' }} onClick={handleReset}>Reset</button>

            <div className="ar-toggle" style={{ height: '36px', flexShrink: 0 }}>
              <button
                className={`ar-toggle-btn ${viewMode === "Day" ? "active" : ""}`}
                onClick={() => setViewMode("Day")}
                style={{ height: '100%' }}
              >Day</button>
              <button
                className={`ar-toggle-btn ${viewMode === "Hour" ? "active" : ""}`}
                onClick={() => setViewMode("Hour")}
                style={{ height: '100%' }}
              >Hour</button>
            </div>

            <div className="ar-filter-group" style={{ height: '36px' }}>
              <input
                type="month"
                value={`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) {
                    const [y, m] = val.split('-');
                    setCurrentDate(new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1));
                  }
                }}
                className="ar-date-input"
                style={{ border: '1px solid #e2e8f0', borderRadius: '6px', height: '100%', padding: '0 8px', fontWeight: 'bold' }}
              />
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0 }}>
              <button className="header-calendar-btn" onClick={() => setShowCalendar(true)} title="Calendar View" style={{ height: '36px' }}>
                <CalendarDays size={16} />
              </button>

              <div className="ar-menu-wrapper" style={{ height: '36px' }}>
                <Tooltip title="Export Options" placement="top">
                  <button className="ar-menu-btn" style={{ height: '100%' }} onClick={() => setShowMenu(!showMenu)}>
                    <Download size={18} />
                  </button>
                </Tooltip>
                {showMenu && (
                  <div className="ar-dropdown">
                    <button className="ar-dropdown-item" onClick={() => { setShowExportModal(true); setShowMenu(false); }}>
                      <Download size={14} /> Export CSV
                    </button>
                    <button className="ar-dropdown-item" onClick={() => { window.print(); setShowMenu(false); }}>
                      <Printer size={14} /> Print
                    </button>
                    <button className="ar-dropdown-item" onClick={downloadPDF}>
                      <FileDown size={14} /> Download PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Status Bar ── */}
          <div className="ar-results-info">
            {loading ? (
              <span className="ar-loading-text"><Loader2 size={14} className="ar-spin" /> Loading...</span>
            ) : error ? (
              <span className="ar-error-text"> {error}</span>
            ) : (
              <>
                Showing <strong>{filteredData.length}</strong> of <strong>{rawData.length}</strong> employees
                {searchQuery && ` — search: "${searchQuery}"`}
                {fromDate && toDate && ` — period: ${fromDate} to ${toDate}`}
              </>
            )}
          </div>

          {/* ── Attendance Grid ── */}
          <div className="ar-grid-wrapper">
            {loading ? (
              <div className="ar-loading-state">
                <Loader2 size={32} className="ar-spin" />
                <p>Fetching attendance records...</p>
              </div>
            ) : error ? (
              <div className="ar-error-state">
                <p>Failed to load data: {error}</p>
                <button className="ar-submit-btn" onClick={fetchAttendanceReport}>Retry</button>
              </div>
            ) : (
              <table className="ar-table">
                <thead>
                  <tr className="ar-header-row">
                    <th className="ar-th ar-sticky-col">Employee</th>
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                      const info = getDayInfo(day);
                      return (
                        <th
                          key={day}
                          className={`ar-th ${info.isWeekend ? "weekend" : ""} ${info.isHoliday ? "holiday-col" : ""}`}
                          title={info.isHoliday ? info.holidayName : ""}
                        >
                          <div className="ar-date-header">{info.date}</div>
                          <div className="ar-day-header">{info.day}</div>
                          {info.isHoliday && <div className="ar-holiday-dot"></div>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan={daysInMonth + 1} className="ar-no-data">
                        {searchQuery
                          ? `No employees found matching "${searchQuery}"`
                          : "No attendance data available"}
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((emp, idx) => (
                      <tr key={emp.id} className={idx % 2 === 0 ? "ar-row-even" : "ar-row-odd"}>

                        {/* ── Employee Cell ── */}
                        <td className="ar-td ar-sticky-col ar-emp-cell">
                          <div className="ar-emp-content">
                            <div className="ar-avatar" style={{ background: emp.color }}>
                              {emp.avatar}
                            </div>
                            <div className="ar-emp-info">
                              <span className="ar-emp-name">{emp.name}</span>
                              <span className="ar-emp-id">{emp.id}</span>
                            </div>
                          </div>
                        </td>

                        {/* ── Day Cells ── */}
                        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                          const dayData = emp.attendance[day] || {
                            status: "-", totalSeconds: 0, hoursDisplay: null,
                          };
                          const info = getDayInfo(day);
                          const status = dayData.status;

                          // Weekend or Holiday → always "-" in Hour view unless they worked
                          const isOffDay =
                            (info.isWeekend || info.isHoliday || status === "W" || status === "H") &&
                            (!dayData.hoursDisplay && status !== "P" && status !== "PC");

                          return (
                            <td
                              key={day}
                              className={`ar-td ar-status-cell
                                ${info.isWeekend ? "weekend" : ""}
                                ${info.isHoliday ? "holiday-col" : ""}`}
                            >
                              {viewMode === "Hour" ? (
                                /* ══ HOUR VIEW ══ */
                                isOffDay ? (
                                  /* Weekend / Holiday → plain dash */
                                  <span className="ar-hour-dash">-</span>
                                ) : dayData.hoursDisplay ? (
                                  /* Has hours → "08:32" with orange underline */
                                  <div className="ar-hour-cell">
                                    <span className="ar-hour-value">{dayData.hoursDisplay}</span>
                                    <div className="ar-hour-bar" />
                                  </div>
                                ) : (
                                  /* No login record → dash with red underline  */
                                  <div className="ar-hour-cell">
                                    <span className="ar-hour-dash">-</span>
                                    <div className="ar-hour-bar ar-hour-bar--absent" />
                                  </div>
                                )
                              ) : (
                                /* ══ DAY VIEW ══ */
                                // <span className={`ar-status ${getStatusStyle(status)}`}>
                                //   {status}
                                // </span>
                                /* ══ DAY VIEW ══ */
                                <span
                                  className={`ar-status ${getStatusStyle(status)}`}
                                  title={dayData.leaveName || ""}
                                >
                                  {status === "L"
                                    ? (dayData.leaveCode || "L")
                                    : status === "HL"
                                      ? `H-${dayData.leaveCode || "L"}`
                                      : status}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Legend ── */}
          {/* <div className="ar-legend">
            <span className="ar-legend-item"><span className="ar-legend-box status-present">P</span> Present</span>
            <span className="ar-legend-item"><span className="ar-legend-box status-partial">PR</span> Permission  </span>
            <span className="ar-legend-item"><span className="ar-legend-box status-absent">A</span> Absent</span>
            <span className="ar-legend-item"><span className="ar-legend-box status-weekend">W</span> Weekend</span>
            <span className="ar-legend-item"><span className="ar-legend-box status-holiday">H</span> Holiday</span>
             <span className="ar-legend-item"><span className="ar-legend-box status-leave">L</span> Leave</span>
            <span className="ar-legend-item"><span className="ar-legend-box status-half-leave">HL</span> Half Leave</span>    
          </div> */}

          <div className="ar-legend">
            <span className="ar-legend-item"><span className="ar-legend-box status-present">P</span> Present</span>
            <span className="ar-legend-item"><span className="ar-legend-box status-partial">PR</span> Permission</span>
            <span className="ar-legend-item"><span className="ar-legend-box status-absent">A</span> Absent</span>
            <span className="ar-legend-item"><span className="ar-legend-box status-pending-checkout">PC</span> Pending Check-out</span>
            <span className="ar-legend-item"><span className="ar-legend-box status-weekend">W</span> Weekend</span>
            <span className="ar-legend-item"><span className="ar-legend-box status-holiday">H</span> Holiday</span>

            {/* ── Dynamic Leave Types ── */}
            {leaveTypes.map(([code, name]) => (
              <span key={code} className="ar-legend-item">
                <span className="ar-legend-box status-leave">{code}</span> {name}
              </span>
            ))}
            {leaveTypes.map(([code, name]) => (
              <span key={`h-${code}`} className="ar-legend-item">
                <span className="ar-legend-box status-half-leave">H-{code}</span> Half {name}
              </span>
            ))}
          </div>

          {/* ── Export Modal ── */}
          {showExportModal && (
            <div className="ar-modal-overlay" onClick={() => setShowExportModal(false)}>
              <div className="ar-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ar-modal-header">
                  <h3 className="ar-modal-title">Export Report</h3>
                  <button className="ar-close-btn" onClick={() => setShowExportModal(false)}><X size={18} /></button>
                </div>
                <div className="ar-modal-body">
                  <p className="ar-modal-label">Choose file format</p>
                  <div className="ar-radio-group">
                    {["CSV", "XLSX"].map((format) => (
                      <label key={format} className="ar-radio-label">
                        <input type="radio" name="format" value={format}
                          checked={exportFormat === format}
                          onChange={(e) => setExportFormat(e.target.value)}
                          className="ar-radio"
                        />
                        <span>{format}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="ar-modal-footer">
                  <button className="ar-export-btn" onClick={executeExport}><Download size={16} /> Export Now</button>
                  <button className="ar-cancel-btn" onClick={() => setShowExportModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </MainLayout>
      {/* Prepare flat attendance list for current user / all users */}
      <CalendarPopup
        visible={showCalendar}
        onClose={() => setShowCalendar(false)}
        mode="attendance"
        holidays={holidays}
        attendance={
          rawData.flatMap(emp =>
            Object.keys(emp.attendance || {}).map(date => ({
              ...emp.attendance[date],
              date,
              employee_name: emp.name
            }))
          )
        }
      />
    </>
  );
}